'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { useRef, useState, useEffect, useCallback } from 'react';

export type VideoPreviewMode = 'auto' | 'hover';

interface VideoPreviewCardProps {
  imageSrc: string;
  videoSrc?: string;
  mode: VideoPreviewMode;
  alt: string;
  children?: React.ReactNode;
  className?: string;
  is_ai_image?: boolean;
}

export function VideoPreviewCard({
  imageSrc,
  videoSrc = '',
  mode,
  alt,
  children,
  className = '',
  is_ai_image = false,
}: VideoPreviewCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Determine if video should be shown
  const shouldShowVideo =
    !videoError &&
    ((mode === 'auto' && isVisible) ||
      (mode === 'hover' && (isHovering || isTouching)));

  // IntersectionObserver để detect visibility
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Reset videoLoaded state when video is unmounted
  useEffect(() => {
    if (!shouldShowVideo) {
      setVideoLoaded(false);
    }
  }, [shouldShowVideo]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Mouse event handlers với debounce
  const handleMouseEnter = useCallback(() => {
    if (mode !== 'hover') return;

    // Debounce 300ms để tránh load video khi user chỉ lướt qua
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
    }, 300);
  }, [mode]);

  const handleMouseLeave = useCallback(() => {
    if (mode !== 'hover') return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovering(false);
  }, [mode]);

  // Touch event handlers
  const handleTouchStart = useCallback(() => {
    if (mode !== 'hover') return;
    setIsTouching(true);
  }, [mode]);

  const handleTouchEnd = useCallback(() => {
    if (mode !== 'hover') return;
    // Keep video playing for a bit after touch ends
    setTimeout(() => {
      setIsTouching(false);
    }, 4000);
  }, [mode]);

  const handleVideoError = useCallback(() => {
    setVideoError(true);
  }, []);

  const handleVideoLoaded = useCallback(() => {
    setVideoLoaded(true);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full aspect-square ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {is_ai_image && (
        <Badge className="z-50 size-7 absolute top-2 right-2 bg-white/50 hover:bg-white/90 text-white border-none shadow-sm p-1 text-[10px] font-bold">
          <Image
            fill
            className="p-1"
            src={`${assetPrefix}/static/icons/white-ai.svg`}
            alt=""
          />
        </Badge>
      )}
      {/* Background Image - always rendered for fallback */}
      <Image
        src={imageSrc}
        alt={alt}
        fill
        className={`object-cover rounded-lg transition-opacity duration-300 ${
          shouldShowVideo && videoLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        sizes="(max-width:768px)50vw,(max-width:1200px)33vw,20vw"
        priority={mode === 'auto'}
      />

      {/* Video layer - only render when needed to save DOM nodes and memory */}
      {/* For hover mode: only mount video element when user is hovering/touching */}
      {/* For auto mode: mount when visible in viewport */}
      {!videoError && videoSrc && shouldShowVideo && (
        <video
          ref={videoRef}
          src={videoSrc}
          className={`absolute inset-0 size-full object-cover rounded-lg transition-opacity duration-300 pointer-events-none ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          muted
          loop
          playsInline
          autoPlay
          disablePictureInPicture
          controls={false}
          preload="auto"
          onError={handleVideoError}
          onLoadedData={handleVideoLoaded}
          style={{
            // Extra inline styles for older iOS Safari
            WebkitTransform: 'translateZ(0)',
          }}
        />
      )}

      {/* Children (ETA badge, etc.) */}
      {children}
    </div>
  );
}
